import { Star, MessageSquarePlus } from 'lucide-react';
import LightningIcon from './icons/LightningIcon';
import ClockIcon from './icons/ClockIcon';

const VuExamenCard = ({ exam, language, activeSessions, isActive, isFavorite, onToggleFavorite, getLocalizedText, onExamAction, onSuggest }) => {
    const isEmpty = exam.vuExamenEmpty || exam.questionsCount === 0;

    return (
        <div
            className={`bg-white dark:bg-[#121212] rounded-2xl p-4 lg:p-6 border-2 flex flex-col lg:flex-row items-start lg:items-center justify-between group transition-all shadow-sm dark:shadow-none gap-4 lg:gap-0 ${
                exam.requiresUpgrade && !isEmpty
                    ? 'opacity-60 grayscale pointer-events-none hover:opacity-80 hover:grayscale-0 border-red-200 dark:border-red-500/30'
                    : isActive
                        ? 'border-blue-400 dark:border-blue-500/60 ring-2 ring-blue-400/30 dark:ring-blue-500/20 hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'
                        : 'border-red-200 dark:border-red-500/30 hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'
            }`}
        >
            <div className="flex items-center gap-4 lg:gap-6 flex-1 w-full lg:w-auto">
                {/* Red icon with maple leaf */}
                <div className="w-14 h-14 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <img src="/img/application/vu_icone.svg" alt="Vu à l'examen" className="w-14 h-14 lg:w-16 lg:h-16" />
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="text-base lg:text-lg font-bold text-gray-900 dark:text-white mb-1 tracking-tight flex items-center gap-3 flex-wrap">
                        {onToggleFavorite && !isEmpty && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onToggleFavorite(exam.id); }}
                                className="flex-shrink-0 p-0.5 transition-colors"
                                title={isFavorite ? (language === 'fr' ? 'Retirer des favoris' : 'Remove from favorites') : (language === 'fr' ? 'Ajouter aux favoris' : 'Add to favorites')}
                            >
                                <Star size={16} className={isFavorite ? 'text-amber-400 fill-amber-400' : 'text-gray-300 dark:text-gray-600 hover:text-amber-300 dark:hover:text-amber-500'} />
                            </button>
                        )}
                        {exam.requiresUpgrade && !isEmpty && (
                            <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        )}
                        {getLocalizedText(exam.title)}
                        {!isEmpty && (
                            <span className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400">
                                {exam.questionsCount} questions
                            </span>
                        )}
                        {exam.requiresUpgrade && !isEmpty && (
                            <span className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400">
                                Premium
                            </span>
                        )}
                        {/* Warning: active session fully answered but not validated */}
                        {!isEmpty && (() => {
                            const session = activeSessions[exam.id];
                            if (!session) return null;
                            const sessionAnswers = session.answers || {};
                            const realAnswers = Object.values(sessionAnswers).filter(a => a !== 0 && a !== '0' && a !== undefined && a !== null).length;
                            const totalQ = exam.questionsCount || 0;
                            if (totalQ === 0 || realAnswers < totalQ) return null;
                            return (
                                <span className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400"
                                    title={language === 'fr' ? 'Session en cours non validée' : 'Unvalidated active session'}
                                >
                                    {language === 'fr' ? 'Non validé' : 'Not validated'}
                                </span>
                            );
                        })()}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 max-w-md">
                        {isEmpty
                            ? (language === 'fr'
                                ? 'Aidez-nous en suggérant des questions vues à l\'examen !'
                                : 'Help us by suggesting questions you saw on the exam!')
                            : (language === 'fr'
                                ? 'Un examen basé sur les questions récentes'
                                : 'An exam based on recent questions')}
                    </p>
                    {/* Minimal stats: only last/best score */}
                    {!isEmpty && (exam.lastScore !== null || exam.bestScore !== null) && (
                        <div className="flex gap-4 mt-2">
                            {exam.lastScore !== null && exam.lastScore !== undefined && (
                                <div className="flex items-center gap-1.5">
                                    <span className="text-[9px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-600">
                                        {language === 'fr' ? 'Dernier' : 'Last'}
                                    </span>
                                    <span className={`text-[10px] font-black ${exam.lastScore >= (exam.minScore || 60) ? 'text-emerald-500' : 'text-red-500'}`}>
                                        {exam.lastScore}%
                                    </span>
                                </div>
                            )}
                            {exam.bestScore !== null && exam.bestScore !== undefined && (
                                <div className="flex items-center gap-1.5">
                                    <span className="text-[9px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-600">
                                        {language === 'fr' ? 'Meilleur' : 'Best'}
                                    </span>
                                    <span className={`text-[10px] font-black ${exam.bestScore >= (exam.minScore || 60) ? 'text-emerald-500' : 'text-red-500'}`}>
                                        {exam.bestScore}%
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2 w-full lg:w-auto">
                {/* Suggest button */}
                {onSuggest && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onSuggest(); }}
                        className="px-3 lg:px-4 py-2 lg:py-2.5 rounded-xl font-black text-xs text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/20 hover:bg-red-50 dark:hover:bg-red-500/10 transition flex items-center gap-1.5 justify-center"
                        title={language === 'fr' ? 'Suggérer une question' : 'Suggest a question'}
                    >
                        <MessageSquarePlus className="w-4 h-4" />
                        <span className="hidden sm:inline">{language === 'fr' ? 'Suggérer' : 'Suggest'}</span>
                    </button>
                )}
                {/* Start/Continue button — only if there are questions */}
                {!isEmpty && (
                    <button
                        onClick={() => onExamAction(exam)}
                        className={`px-4 lg:px-6 py-2 lg:py-2.5 rounded-xl font-black text-xs shadow-xl transition hover:scale-105 active:scale-95 flex items-center gap-2 flex-1 lg:flex-none justify-center ${
                            exam.requiresUpgrade
                                ? 'pointer-events-auto text-amber-700 dark:text-amber-300 bg-gradient-to-r from-amber-100 to-amber-200 dark:from-amber-500/20 dark:to-amber-600/20 shadow-amber-500/20 border border-amber-300 dark:border-amber-500/30'
                                : 'text-white bg-gradient-to-r from-red-600 to-red-700 shadow-red-500/20'
                        }`}
                    >
                        {exam.requiresUpgrade ? (
                            <>
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                {language === 'fr' ? 'Débloquer' : 'Unlock'}
                            </>
                        ) : (
                            <>
                                {activeSessions[exam.id] && (
                                    activeSessions[exam.id].mode === 'flash'
                                        ? <LightningIcon className="w-4 h-4" />
                                        : <ClockIcon className="w-4 h-4" />
                                )}
                                {activeSessions[exam.id]
                                    ? (language === 'fr' ? 'Continuer' : 'Continue')
                                    : (exam.lastScore !== null || exam.bestScore !== null)
                                        ? (language === 'fr' ? 'Recommencer' : 'Restart')
                                        : (language === 'fr' ? 'Commencer' : 'Start')}
                            </>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
};

export default VuExamenCard;
